import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const stages = [
  { x: 70, y: 249, label: "Unknown", color: "#5A6272", ly: 274 },
  { x: 230, y: 224, label: "Emerging", color: "#8B93A3", ly: 249 },
  { x: 390, y: 152, label: "Early Conviction", color: "#2BD97C", ly: 130 },
  { x: 570, y: 75, label: "Consensus", color: "#E8EBF0", ly: 53 },
  { x: 700, y: 55, label: "Crowded", color: "#E3A44A", ly: 33 },
  { x: 830, y: 112, label: "Fading", color: "#C75E5E", ly: 140 },
];

const STAGE_DEFS = [
  { label: "Emerging", color: "#8B93A3", def: "The first graded specialists show up in the filings. On the radar, not yet a case." },
  { label: "Early Conviction", color: "#2BD97C", def: "Several proven specialists converge, independently, while the tape is still quiet. The head start." },
  { label: "Consensus", color: "#E8EBF0", def: "Participation broadens and the tape picks up. The market is catching on." },
  { label: "Crowded", color: "#E3A44A", def: "On the lockstep crowding list: too many institutions in the same trade at once. Warnings fire here." },
  { label: "Fading", color: "#C75E5E", def: "The evidence unwinds. Specialists trim, support fades. The record shows what tends to follow." },
];

export default function ConvictionCurve({ withStages = false }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/stages/summary`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => d.as_of && setSummary(d))
      .catch(() => {});
  }, []);

  return (
    <section id="curve" className="border-y border-[rgba(255,255,255,0.06)] bg-[#0D111A]">
      <div className="fp-section">
        <p className="fp-eyebrow mb-6">The Conviction Curve</p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
          Every stock sits somewhere on this curve.
        </h2>
        <p className="mt-5 max-w-2xl text-base text-[#8B93A3] leading-relaxed">
          Most people buy after Consensus. That's the definition of late. The evidence is legible
          long before. Fundprints tells you the stage today, and re-reads it every week, because
          trades move both ways: names cool off, get re-accumulated, heat up again.
        </p>
        <p data-testid="curve-runway-stat" className="mt-4 max-w-2xl font-mono text-[12px] text-[#2BD97C] leading-relaxed">
          In 13 months of weekly reads, names that turned Crowded or Fading had typically been on
          the radar 13 weeks earlier, at a healthier stage. That's the runway.
        </p>
        <div data-testid="conviction-curve-card" className="mt-12 fp-card p-5 sm:p-10 relative">
          <svg viewBox="0 0 900 310" className="w-full h-auto" role="img" aria-label="The Conviction Curve: Unknown, Emerging, Early Conviction, Consensus, Crowded, Fading">
            {/* highlight band: where Fundprints lives */}
            <rect x="300" y="20" width="180" height="260" rx="6" fill="rgba(43,217,124,0.07)" />
            <text x="390" y="300" textAnchor="middle" fill="#2BD97C" fontSize="11" fontFamily="'JetBrains Mono', monospace" letterSpacing="2">
              FUNDPRINTS LIVES HERE
            </text>
            <line x1="40" y1="280" x2="870" y2="280" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
            <path
              d="M40 252 C160 246 300 210 390 152 C470 102 510 82 570 75 C630 68 670 46 700 55 C750 70 790 88 870 128"
              fill="none"
              stroke="rgba(232,235,240,0.85)"
              strokeWidth="2"
            />
            {stages.map((s) => (
              <g key={s.label}>
                <circle cx={s.x} cy={s.y} r="6" fill={s.color} />
                <circle cx={s.x} cy={s.y} r="11" fill="none" stroke={s.color} strokeOpacity="0.35" strokeWidth="1.5" />
                <text
                  x={s.x}
                  y={s.ly}
                  textAnchor="middle"
                  fill={s.color}
                  fontSize="12.5"
                  fontWeight="600"
                  fontFamily="'JetBrains Mono', monospace"
                >
                  {s.label}
                </text>
              </g>
            ))}
          </svg>
          <span className="absolute bottom-3 right-4 font-mono text-[10px] text-[#3A4152]">
            fundprints.com
          </span>
        </div>
        {summary && (
          <p data-testid="curve-live-counts" className="mt-5 font-mono text-[12px] text-[#8B93A3] leading-relaxed">
            <span className="text-[#5A6272] uppercase tracking-widest text-[10px] mr-3">On the curve right now</span>
            {["Emerging", "Early Conviction", "Consensus", "Crowded", "Fading"]
              .filter((s) => summary.counts[s])
              .map((s, i, arr) => (
                <span key={s}>
                  <span style={{ color: { "Early Conviction": "#2BD97C", Crowded: "#E3A44A", Fading: "#C75E5E" }[s] || "#E8EBF0" }}>
                    {summary.counts[s]} {s}
                  </span>
                  {i < arr.length - 1 && <span className="text-[#3A4152]"> · </span>}
                </span>
              ))}
            <span className="text-[#5A6272]"> · live, as of {summary.as_of}</span>
          </p>
        )}
        <p data-testid="curve-crowd-definition" className="mt-3 font-mono text-[11px] text-[#5A6272] max-w-3xl leading-relaxed">
          How we define crowd arrival: the stock sits in the top decile of lockstep crowding
          across institutional books, or its accumulation has peaked by our regime read: a stable,
          auditable definition, not a feeling. The options tape (Quiet / Neutral / Hot) is shown
          alongside as its own signal, not as a stage.
        </p>
        {withStages && (
          <div data-testid="curve-stage-defs" className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {STAGE_DEFS.map((s) => (
              <div key={s.label} className="fp-card p-5">
                <p className="font-mono text-[12px] font-semibold tracking-wide" style={{ color: s.color }}>
                  {s.label}
                </p>
                <p className="mt-2.5 text-[12.5px] text-[#8B93A3] leading-relaxed">{s.def}</p>
              </div>
            ))}
          </div>
        )}
        {withStages && (
          <a
            href="/how"
            data-testid="curve-full-mechanics-link"
            className="inline-block mt-8 font-mono text-[13px] text-[#2BD97C] hover:underline"
          >
            The full mechanics, explained →
          </a>
        )}
      </div>
    </section>
  );
}
